import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import rolesPermissionsService from "../services/rolesPermissionsService";
import styles from "../assets/crmRolesPermissions.module.css";

const CrmRolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [notAllowedPermissions, setNotAllowedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRolesPermissions();
  }, []);

  const loadRolesPermissions = async () => {
    try {
      setLoading(true);
      const response = await rolesPermissionsService.getRolesPermissions();
      
      if (response.success) {
        setRoles(response.data.roles);
        setPermissions(response.data.permissions);
        setNotAllowedPermissions(response.data.notAllowedPermissions || []);
        
        // Initialize role permissions matrix
        const initialRolePermissions = {};
        response.data.roles.forEach(role => {
          initialRolePermissions[role.id] = role.permissions || [];
        });
        setRolePermissions(initialRolePermissions);
        
      } else {
        throw new Error(response.message || 'Failed to load roles and permissions');
      }
    } catch (error) {
      console.error("Failed to load roles and permissions:", error);
      
      // Fallback to mock data
      const mockRoles = [
        { id: 1, name: "SYSTEM-ADMINISTRATOR", display_name: "System Administrator", permissions: [1, 2, 3, 4, 5, 6, 7, 8] },
        { id: 2, name: "SENIOR-MANAGEMENT", display_name: "Senior Management", permissions: [4, 5, 6] },
        { id: 3, name: "CLIENT", display_name: "Client", permissions: [7, 8] }
      ];
      
      const mockPermissions = [
        { id: 1, display_name: "Manage Users", name: "users.manage", route: "users" },
        { id: 2, display_name: "View Users", name: "users.view", route: "users" },
        { id: 3, display_name: "Manage Roles", name: "roles.manage", route: "roles" },
        { id: 4, display_name: "View Roles", name: "roles.view", route: "roles" },
        { id: 5, display_name: "Manage Permissions", name: "permissions.manage", route: "permissions" },
        { id: 6, display_name: "View Permissions", name: "permissions.view", route: "permissions" },
        { id: 7, display_name: "Manage Guardians", name: "guardians.manage", route: "guardians" },
        { id: 8, display_name: "View Guardians", name: "guardians.view", route: "guardians" }
      ];
      
      setRoles(mockRoles);
      setPermissions(mockPermissions);
      
      // Mock not allowed permissions
      const mockNotAllowedPermissions = ["users.manage", "roles.manage", "permissions.manage"];
      setNotAllowedPermissions(mockNotAllowedPermissions);
      
      // Initialize role permissions matrix
      const initialRolePermissions = {};
      mockRoles.forEach(role => {
        initialRolePermissions[role.id] = role.permissions || [];
      });
      setRolePermissions(initialRolePermissions);
      
      toast.warning("Using mock data - backend unavailable");
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (roleId, permissionId, checked) => {
    setRolePermissions(prev => {
      const updated = { ...prev };
      if (checked) {
        updated[roleId] = [...(updated[roleId] || []), permissionId];
      } else {
        updated[roleId] = (updated[roleId] || []).filter(id => id !== permissionId);
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await rolesPermissionsService.updateRolePermissions(rolePermissions);

      if (response.success) {
        toast.success(response.message || "Role permissions updated successfully!");
      } else {
        throw new Error(response.message || 'Failed to update role permissions');
      }
    } catch (error) {
      console.error("Failed to update role permissions:", error);
      toast.error("Failed to update role permissions");
    } finally {
      setLoading(false);
    }
  };

  if (loading && roles.length === 0) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <div className="panel panel-default panel-standard">
              <div className="panel-body text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="/dashboard">Dashboard</a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            All roles permissions
          </li>
        </ol>
      </nav>

      <div className="row">
        <div className="col-md-12">
          <div className="panel panel-default panel-standard">
            <div className="panel-heading panel-title">
              <h3>All roles permissions</h3>
            </div>
            <div className="panel-body">
              <form onSubmit={handleSubmit}>
                <div className={styles["table-responsive"]}>
                  <table className={styles["table-hover"]}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Route</th>
                        {roles.map((role) => (
                          <th key={role.id} className="text-center">
                            {role.display_name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {permissions.map((permission) => (
                        <tr key={permission.id}>
                          <td width="380px">{permission.display_name}</td>
                          <td><small className={`${styles.label} ${styles["label-info"]}`}>{permission.route}</small></td>
                          {roles.map((role) => (
                            <td key={role.id} className={styles["text-center"]} width="150">
                              {role.name === "SYSTEM-ADMINISTRATOR" && rolePermissions[role.id]?.includes(permission.id) ? (
                                <>
                                  <input
                                    type="checkbox"
                                    checked={true}
                                    disabled={true}
                                    className={styles["permission-checkbox"]}
                                  />
                                  <input
                                    type="hidden"
                                    name={`roles[${role.id}][permissions][]`}
                                    value={permission.id}
                                  />
                                </>
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={rolePermissions[role.id]?.includes(permission.id) || false}
                                  onChange={(e) => handlePermissionChange(role.id, permission.id, e.target.checked)}
                                  disabled={
                                    (notAllowedPermissions.includes(permission.name) && role.name !== "SYSTEM-ADMINISTRATOR")
                                  }
                                  name={`roles[${role.id}][permissions][]`}
                                  value={permission.id}
                                  className={styles["permission-checkbox"]}
                                />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="form-group pull-right" style={{ marginTop: '20px' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmRolesPermissions; 